import { useState } from "react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { cn } from "@/lib/utils";
import { fetchJson } from "@/lib/queryClient";
import { useFluxoOperador } from "@/hooks/use-fluxo-operador";
import { BarraAtualizacao } from "./barra-atualizacao";
import type { MnjOperacaoWithItens } from "@shared/schema";
import {
  useMinhasOperacoes, useCancelarOperacao, useReabrirOperacao, useComentarOperacao, type ItemMinhaView,
} from "@/hooks/use-manejo";
import { useToast } from "@/hooks/use-toast";
import { StatusBadge } from "@/components/data/status-badge";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import { selosDoItem } from "@shared/manejo-selos";
import { ROTULO_INICIAR, ROTULO_CONCLUIR } from "@shared/manejo-vocabulario";
import { FolhaConclusao } from "./folha-conclusao";
import { ROTULO_DESFAZER } from "@shared/manejo-vocabulario";
import { OperacaoDetalhe } from "./operacao-detalhe";
import { NovaOperacaoDialog } from "./nova-operacao-dialog";
import { usePode } from "@/lib/permissoes";
import { ClipboardCheck, Check, ChevronDown, ChevronRight, Play, UserX } from "lucide-react";

// Guia MINHAS — a tela do operador: o que está atribuído a ele, em todas as frentes.
// Cards grandes, botões de largura total. "Feito" conclui DIRETO (sem folha de
// confirmação) e o aviso "Feito · Desfazer" fica 8 s na tela; item com insumo abre a
// folha de quantidades. Tudo otimista e com "salvando" por item (useFluxoOperador).

export function MinhasOperacoes() {
  const { data, isLoading, dataUpdatedAt, isFetching, refetch } = useMinhasOperacoes();
  const fluxo = useFluxoOperador();
  const { toast } = useToast();
  const [comInsumos, setComInsumos] = useState<MnjOperacaoWithItens | null>(null);
  const [detalheId, setDetalheId] = useState<string | null>(null);
  // "Depois" (data futura) começa recolhida: não é para agora.
  const [depoisAberto, setDepoisAberto] = useState(false);
  // Editar é de quem planeja (a API recusa o operador); o operador reagenda pelo detalhe.
  const podePlanejar = usePode("manejo.planejar");
  const [editando, setEditando] = useState<MnjOperacaoWithItens | null>(null);
  const cancelar = useCancelarOperacao();
  const reabrir = useReabrirOperacao();
  const comentar = useComentarOperacao();

  const pedirFeito = async (item: ItemMinhaView) => {
    if (!item.comInsumos) { await fluxo.concluir(item.id); return; }
    // Insumo previsto: as quantidades realizadas moram na folha de conclusão.
    try {
      setComInsumos(await fetchJson<MnjOperacaoWithItens>(`/api/manejo/operacoes/${item.id}`));
    } catch (e) {
      fluxo.erroNaTela(e);
    }
  };

  if (isLoading) return <PageSkeleton variante="cards" linhas={3} />;

  if (data?.semVinculo) {
    return (
      <EmptyState
        icone={UserX}
        titulo="Seu usuário não está vinculado a um funcionário"
        descricao="Peça ao administrador para ligar o seu usuário ao seu cadastro de funcionário."
        testId="empty-sem-vinculo"
      />
    );
  }

  const itens = data?.itens ?? [];
  const feitos = data?.feitosHoje ?? [];
  const hoje = hojeNaFazenda();

  // O nome da frente só ajuda quando os itens vêm de mais de uma.
  const nomesDeFrente: Record<string, true> = {};
  itens.forEach(i => { nomesDeFrente[i.frenteNome ?? ""] = true; });
  const variasFrentes = Object.keys(nomesDeFrente).length > 1;

  return (
    <div className="space-y-4">
      <BarraAtualizacao dataUpdatedAt={dataUpdatedAt} buscando={isFetching} onAtualizar={() => { void refetch(); }} />

      {itens.length === 0 ? (
        <EmptyState icone={ClipboardCheck} titulo="Nada atribuído a você" descricao="Quando alguém passar um item para você, ele aparece aqui." testId="empty-minhas" />
      ) : (
        <div className="space-y-5" data-testid="lista-minhas">
          {SECOES.map(sec => {
            const daSecao = itens.filter(i => i.secao === sec.chave);
            if (daSecao.length === 0) return null;
            const recolhida = sec.recolhivel && !depoisAberto;
            return (
              <section key={sec.chave} className="space-y-2" data-testid={`secao-minhas-${sec.chave}`}>
                {sec.recolhivel ? (
                  <button
                    type="button" onClick={() => setDepoisAberto(a => !a)} aria-expanded={!recolhida}
                    className="flex min-h-11 w-full items-center gap-1 text-left text-sm font-semibold text-muted-foreground"
                  >
                    {recolhida ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    {sec.titulo} · {daSecao.length}
                  </button>
                ) : (
                  <h2 className="text-sm font-semibold text-muted-foreground">{sec.titulo} · {daSecao.length}</h2>
                )}
                {!recolhida && (
                  <ul className="space-y-3">
                    {daSecao.map(item => (
                      <CartaoMinha
                        key={item.id} item={item} hoje={hoje} mostrarFrente={variasFrentes}
                        salvando={!!fluxo.pendentes[item.id]}
                        onAbrir={() => setDetalheId(item.id)}
                        onIniciar={() => { void fluxo.iniciar(item.id); }}
                        onFeito={() => { void pedirFeito(item); }}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {feitos.length > 0 && (
        <section className="space-y-1">
          <h2 className="text-sm font-semibold text-muted-foreground">Feitos hoje · {feitos.length}</h2>
          <ul className="divide-y rounded-md border">
            {feitos.map(i => (
              <li key={i.id} className="flex items-center gap-2 px-3 py-2">
                <span className="min-w-0 flex-1 text-sm text-muted-foreground line-through">{i.tituloExibicao}</span>
                {i.desfazivelPorMim && (
                  <Button variant="ghost" size="sm" className="min-h-11" disabled={!!fluxo.pendentes[i.id]}
                    onClick={() => { void fluxo.desfazer(i.id); }} data-testid={`minha-desfazer-${i.id}`}>
                    {ROTULO_DESFAZER}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <OperacaoDetalhe
        operacaoId={detalheId}
        onOpenChange={a => !a && setDetalheId(null)}
        onIniciar={op => { void fluxo.iniciar(op.id); setDetalheId(null); }}
        onConcluir={op => {
          setDetalheId(null);
          if (op.itens.length > 0) setComInsumos(op); else void fluxo.concluir(op.id);
        }}
        onReabrir={op => { void reabrir.mutateAsync(op.id).then(() => toast({ title: "Operação reaberta" })).catch(fluxo.erroNaTela); }}
        onCancelar={op => {
          const motivo = window.prompt("Por que está cancelando? (fica na linha do tempo)");
          if (!motivo?.trim()) return;
          setDetalheId(null);
          void cancelar.mutateAsync({ id: op.id, motivo: motivo.trim() }).then(() => toast({ title: "Item cancelado" })).catch(fluxo.erroNaTela);
        }}
        onEditar={podePlanejar ? (op => { setDetalheId(null); setEditando(op); }) : undefined}
        onComentar={(op, texto) => { void comentar.mutateAsync({ id: op.id, texto }).then(() => toast({ title: "Comentário registrado" })).catch(fluxo.erroNaTela); }}
      />

      <NovaOperacaoDialog
        open={!!editando}
        onOpenChange={aberto => !aberto && setEditando(null)}
        operacao={editando}
      />

      <FolhaConclusao
        operacao={comInsumos}
        onOpenChange={a => !a && setComInsumos(null)}
        onConfirmar={async dados => {
          if (!comInsumos) return;
          const ok = await fluxo.concluir(comInsumos.id, dados);
          if (ok) setComInsumos(null);
        }}
        salvando={!!(comInsumos && fluxo.pendentes[comInsumos.id])}
      />
    </div>
  );
}

const SECOES: Array<{ chave: "fazendo" | "hoje" | "fila" | "depois"; titulo: string; recolhivel?: boolean }> = [
  { chave: "fazendo", titulo: "Fazendo agora" },
  { chave: "hoje", titulo: "Para hoje" },
  { chave: "fila", titulo: "Na fila" },
  { chave: "depois", titulo: "Depois", recolhivel: true },
];

function CartaoMinha({ item, hoje, mostrarFrente, salvando, onAbrir, onIniciar, onFeito }: {
  item: ItemMinhaView;
  hoje: string;
  mostrarFrente: boolean;
  salvando: boolean;
  onAbrir: () => void;
  onIniciar: () => void;
  onFeito: () => void;
}) {
  const selos = selosDoItem(item, hoje);
  const fazendo = item.status === "em_andamento";
  return (
    <li className={cn("rounded-lg border p-4 space-y-3", fazendo && "bg-muted/40")} data-testid={`minha-${item.id}`}>
      {/* O corpo do cartão abre o detalhe (instruções, setores, insumos, linha do tempo). */}
      <button type="button" onClick={onAbrir} className="block w-full space-y-1.5 text-left" data-testid={`minha-abrir-${item.id}`}>
        <span className="block text-base font-semibold leading-snug">{item.tituloExibicao}</span>
        {(selos.length > 0 || item.local || (mostrarFrente && item.frenteNome)) && (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            {selos.map(sl => <StatusBadge key={sl.chave} tom={sl.tom}>{sl.texto}</StatusBadge>)}
            {mostrarFrente && item.frenteNome && <span>{item.frenteEmoji ? `${item.frenteEmoji} ` : ""}{item.frenteNome}</span>}
            {item.local && <span>{item.local}</span>}
          </span>
        )}
        {item.observacao && (
          <span className="block text-sm text-muted-foreground line-clamp-2" data-testid={`minha-obs-${item.id}`}>{item.observacao}</span>
        )}
      </button>
      {/* Não iniciado: "Fazendo" é o primário e "Feito" o secundário, lado a lado.
          Em andamento: só "Feito", primário. */}
      <div className={fazendo ? "grid grid-cols-1 gap-2" : "grid grid-cols-2 gap-2"}>
        {!fazendo && (
          <Button className="min-h-12" disabled={salvando} onClick={onIniciar} data-testid={`minha-fazendo-${item.id}`}>
            <Play className="h-4 w-4 mr-2" />{ROTULO_INICIAR}
          </Button>
        )}
        <Button variant={fazendo ? "default" : "outline"} className="min-h-12" disabled={salvando} onClick={onFeito} data-testid={`minha-feito-${item.id}`}>
          <Check className="h-4 w-4 mr-2" />{ROTULO_CONCLUIR}
        </Button>
      </div>
    </li>
  );
}
